import React, { useMemo } from 'react'
import { TextFieldProps } from '@mui/material'
import block from 'bem-cn'

import { LocalePaymentKeysType, LocalePaymentType } from 'shared/locale/en-US'
import { ExtraSchema } from 'shared/models/Payments'
import { replaceLetters } from 'shared/utils/payments'

import { DatePicker, FormDatePicker, FormInput, Input } from 'components/UIKit'
import { AutoCompleteComponent } from 'components/UIKit/AutoComplete/AutoComplete'
import TimePicker from 'components/UIKit/TimePicker/TimePicker'

import { dateFormatRequest, timeFormatRequest } from '../../data/utils'

import './PaymentFieldGroup.scss'

type IPaymentField = TextFieldProps & {
  field: ExtraSchema
  title: LocalePaymentKeysType
  locale: LocalePaymentType
  value: string | number
  touched: Record<string, boolean | undefined>
  errors: Record<string, string | undefined>
  endAdornment?: React.ReactNode
  handleChange: (v: string) => void
  setFieldValue: (field: string, value: string) => void
}

const b = block('payment-field-group')

export const PaymentFieldGroup: React.FC<IPaymentField> = props => {
  const { field, title, locale, value, touched, errors, endAdornment, handleChange, onBlur, setFieldValue } = props

  const filedByType = useMemo(() => {
    switch (field.fieldType) {
      case 'input':
        return (
          <>
            <div className={b('input-wrapper')}>
              <FormInput
                name={field.name}
                type={field.type}
                caption={locale[title.toLowerCase().replaceAll(' ', '_') as LocalePaymentKeysType] || title}
                suffix={endAdornment}
                disabled={field.isDisabled}
                placeholder={
                  (locale[field.placeholder?.toLowerCase().replaceAll(' ', '_') as LocalePaymentKeysType] as string) ??
                  field.placeholder ??
                  (locale[title.toLowerCase().replaceAll(' ', '_') as LocalePaymentKeysType] as string) ??
                  title
                }
                spellCheck={false}
              />
            </div>
          </>
        )
      case 'selector':
        return (
          <>
            {field.name === 'cashier_id' && <div className={b('label')}>{locale.selectCashier}</div>}
            <div className={b('autocomplete')}>
              <AutoCompleteComponent
                data={field.values.map(f => {
                  const name = (locale[f.key as LocalePaymentKeysType] as string) ?? f.title ?? f.key

                  return {
                    name,
                    value: f.value,
                  }
                })}
                value={value as string}
                defaultValue=""
                onSelect={v => setFieldValue(field.name, v)}
              />
            </div>
          </>
        )

      case 'date':
        return (
          <div className={b('date')}>
            <div className={b('date-input')}>
              <FormDatePicker name={field.name} caption={locale.date} />
            </div>
            <div className={b('date-input')}>
              <label className={b('date-label')}>{locale.time}</label>
              <TimePicker
                callback={v => setFieldValue(field.name, value.toString().replace(/T\d{2}:\d{2}:\d{2}/, `T${v}`))}
                format={timeFormatRequest}
                defaultValue={new Date(value)}
                calendarDefaultDate={new Date(value)}
                size="md"
                block
              />
            </div>
          </div>
        )

      default:
        return null
    }
  }, [endAdornment, field, handleChange, locale, onBlur, setFieldValue, title, value])

  if (filedByType === null) return null

  return (
    <div className={b()} key={field.name}>
      <div
        className={b('input', {
          error: Boolean(touched[field.name] && errors[field.name]),
          correct: Boolean(touched[field.name] && !errors[field.name]),
          type: field.fieldType,
        })}>
        {filedByType}
      </div>
    </div>
  )
}
